import { ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react';

import { Shimmer, TableV2, Text } from '@pukka/ui-components';
import { twMerge } from 'tailwind-merge';

import Styles from './style.module.css';

interface TransactionDetailSectionProps {
  readonly title: string;
  readonly className?: string;
  readonly children: ReactNode;
}

interface TransactionDetailRowProps {
  readonly title: string;
  readonly loading?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}

interface TransactionDetailTableProps {
  readonly title: string;
  readonly loading?: boolean;
  readonly rounding?: 'topbottom' | 'top' | 'bottom' | '';
  readonly className?: string;
  readonly children?: ReactNode;
}

interface TransactionDetailTable2Props {
  readonly header: ReactNode;
  readonly columnCount?: number;
  readonly className?: string;
  readonly children?: ReactNode;
}

function TransactionDetailSection({ title, className, children }: TransactionDetailSectionProps) {
  return (
    <div className={twMerge('mb-10', className)}>
      <Text as="h3" variant="subtitle-1" weight="bold" className="mb-5 border-b pb-3">
        {title}
      </Text>
      {children}
    </div>
  );
}

function TransactionDetailRow({ title, loading = false, className, children }: TransactionDetailRowProps) {
  return (
    <div className={twMerge('flex flex-row items-center justify-between py-2.5', className)}>
      <Text variant="body-1" weight="medium">
        {title}
      </Text>
      {/* eslint-disable-next-line no-nested-ternary */}
      {typeof children === 'string' || typeof children === 'number' ? (
        loading ? (
          <Shimmer size="sm" />
        ) : (
          <Text variant="body-1" className="text-right">
            {children}
          </Text>
        )
      ) : (
        children
      )}
    </div>
  );
}

function TransactionDetailTableHeader({ children, className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <TableV2.Cell as="th" {...props} className={twMerge('px-0 pb-7 pt-2.5', className)}>
      {children}
    </TableV2.Cell>
  );
}

function TransactionDetailTableCell({ children, className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <TableV2.Cell {...props} className={twMerge('whitespace-pre-wrap border-neutral-200 bg-white', className)}>
      {children}
    </TableV2.Cell>
  );
}

function TransactionDetailTable2({ header, columnCount, className, children }: TransactionDetailTable2Props) {
  return (
    <TableV2 size="sm" className={twMerge('border-separate bg-neutral-50', Styles.table, className)}>
      <TableV2.Section as="header">{header}</TableV2.Section>
      <TableV2.Section className="bg-white">
        {children}
        {Array.isArray(children) && children.length === 0 ? (
          <TableV2.Row className="text-neutral-400">
            <TransactionDetailTableCell colSpan={columnCount}>No Item Found</TransactionDetailTableCell>
          </TableV2.Row>
        ) : null}
      </TableV2.Section>
    </TableV2>
  );
}

function TransactionDetailTable({ title, loading, rounding = 'topbottom', className, children }: TransactionDetailTableProps) {
  return (
    <div
      className={twMerge(
        'flex flex-row items-stretch border-x border-t border-neutral-200 bg-white text-neutral-400',
        rounding.match('top') && 'rounded-t',
        rounding.match('bottom') && 'rounded-b border-b',
        className,
      )}
    >
      {loading ? (
        <>
          <div className="flex-1 p-3">
            <Shimmer size="sm" className="w-24" />
          </div>
          <div className="flex w-32 justify-end border-l border-neutral-200 p-3">
            <Shimmer size="sm" className="w-8" />
          </div>
        </>
      ) : (
        <>
          <Text variant="body-1" className="flex-1 whitespace-pre-wrap p-3">
            {title}
          </Text>
          {typeof children === 'string' || typeof children === 'number' ? (
            <Text variant="body-1" className="w-32 border-l border-neutral-200 p-3 text-right">
              {children}
            </Text>
          ) : (
            children
          )}
        </>
      )}
    </div>
  );
}

TransactionDetailSection.Row = TransactionDetailRow;
TransactionDetailSection.Table = TransactionDetailTable;

TransactionDetailTable2.Row = TableV2.Row;
TransactionDetailTable2.Cell = TransactionDetailTableCell;
TransactionDetailTable2.Header = TransactionDetailTableHeader;

export { TransactionDetailSection, TransactionDetailTable2 as TransactionDetailTable };
